import React from 'react';
import { Button, Modal } from '@nsi-advisory-services/components';

import { getCategoryLabel } from '../../utils/expenseCategories';

// What has changed about a saved run's expenses since it was filed, and the way
// to take it.
//
// A saved run is frozen so that what somebody was paid against cannot quietly
// change under them. But expenses are approved and marked paid *during* payroll,
// so the freeze has to have a door in it. This is that door: it shows exactly
// what moved, and syncing is an explicit act rather than something the page does
// on its own. Nothing here recalculates hours or pay - only the reimbursement
// figures and Cash Needed move.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDelta = (value) => {
  const n = Number(value) || 0;
  return `${n > 0 ? '+' : ''}${fmtMoney(n)}`;
};

const STAGE_LABEL = {
  paid: 'paid',
  unpaid: 'approved, unpaid',
  pending: 'awaiting approval',
};

const describeItem = (item) => item.vendor || getCategoryLabel(item.category) || item.description || 'Expense';

// One line of "what happened to this expense". Written as a sentence rather than
// a before/after pair of columns because the interesting part is the move -
// "awaiting approval to approved, unpaid" is the thing somebody just did.
const changeText = (item) => {
  if (item.change === 'added') return `new · ${STAGE_LABEL[item.stage] || item.stage}`;
  if (item.change === 'removed') return 'no longer on this run';
  if (item.fromStage && item.fromStage !== item.stage) {
    return `${STAGE_LABEL[item.fromStage] || item.fromStage} → ${STAGE_LABEL[item.stage] || item.stage}`;
  }
  return `amount changed from ${fmtMoney(item.fromAmount)}`;
};

export default function PayrollReimbursementDriftModal({
  open,
  drift,
  onClose,
  onSync,
  syncing = false,
}) {
  const employees = drift?.employees || [];
  const outsideRun = drift?.outsideRun || [];
  const delta = Number(drift?.totals?.delta) || 0;

  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>Expenses have changed since this run was filed</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
          Syncing updates the reimbursement figures and Cash Needed on this run.
          Hours, rates, compensation, retirement and the journal entry are not
          touched.
        </p>

        {employees.length === 0 ? (
          <p className="text-muted" style={{ fontSize: 13 }}>
            Nothing to sync &mdash; this run's reimbursements match live expenses.
          </p>
        ) : (
          <div className="payroll-drift">
            {employees.map((row) => (
              <div className="payroll-drift__person" key={row.employeeId}>
                <div className="payroll-drift__head">
                  <span className="payroll-drift__name">{row.name}</span>
                  <span className="payroll-drift__totals bdc-num">
                    {fmtMoney(row.before.amount)}
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                    {fmtMoney(row.after.amount)}
                    <span
                      className={`payroll-drift__delta${row.delta < 0 ? ' payroll-drift__delta--down' : ''}`}
                    >
                      {fmtDelta(row.delta)}
                    </span>
                  </span>
                </div>

                <ul className="payroll-drift__items">
                  {[...row.items.added, ...row.items.changed, ...row.items.removed].map((item) => (
                    <li className={`payroll-drift__item payroll-drift__item--${item.change}`} key={item.id}>
                      <span className="payroll-drift__itemName">{describeItem(item)}</span>
                      <span className="payroll-drift__itemChange">{changeText(item)}</span>
                      <span className="payroll-drift__itemAmount bdc-num">{fmtMoney(item.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="payroll-drift__foot">
              <span>Reimbursements total</span>
              <span className="bdc-num">
                {fmtMoney(drift?.totals?.before)}
                <i className="bi bi-arrow-right" aria-hidden="true" />
                {fmtMoney(drift?.totals?.after)}
                <span className={`payroll-drift__delta${delta < 0 ? ' payroll-drift__delta--down' : ''}`}>
                  {fmtDelta(delta)}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Reported, not acted on. Adding a column would change the shape of a
            run somebody may already have paid against, so the honest answer is
            to delete the run and pull the period again. */}
        {outsideRun.length > 0 ? (
          <div className="payroll-drift__warning">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
            {outsideRun.length} employee{outsideRun.length === 1 ? ' has' : 's have'} expenses
            for this period but {outsideRun.length === 1 ? 'is' : 'are'} not on this run.
            Syncing will not add {outsideRun.length === 1 ? 'them' : 'them'} &mdash; delete the run
            and pull the period again to include {outsideRun.length === 1 ? 'them' : 'them'}.
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose} disabled={syncing}>
          Not now
        </Button>
        <Button
          variant="primary"
          onClick={onSync}
          loading={syncing}
          disabled={employees.length === 0}
        >
          Sync reimbursements
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
